import { useCallback, useEffect, useRef } from 'react';
import { moduleTimeTrackingAPI } from '../services/api';

interface UseModuleTimeTrackerProps {
  courseId: string;
  chapterId: string;
  moduleType: 'capsule' | 'quiz' | 'workshop';
  moduleId: string;
  enabled?: boolean;
  /** Interval in seconds to persist time while actively tracking (default: 60). */
  saveInterval?: number;
  /**
   * Pause tracking after this many ms without interaction on this page.
   * Default 20 minutes — allows watching videos without constant input.
   * Activity in other tabs does not count.
   */
  idleTimeoutMs?: number;
}

const DEFAULT_IDLE_TIMEOUT_MS = 20 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const IDLE_CHECK_MS = 15_000;

/**
 * Track time spent on a learning module.
 * Pauses after `idleTimeoutMs` with no interaction on this page (mousemove, scroll, etc.),
 * and resumes on the next activity. Time in a background tab still counts only until idle timeout.
 */
export const useModuleTimeTracker = ({
  courseId,
  chapterId,
  moduleType,
  moduleId,
  enabled = true,
  saveInterval = 60,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
}: UseModuleTimeTrackerProps) => {
  const segmentStartRef = useRef<number | null>(null);
  const lastSavedRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const pausedRef = useRef(false);
  const lastActivityHandledAtRef = useRef(0);

  const getEffectiveNow = useCallback(() => {
    const idleDeadline = lastActivityRef.current + idleTimeoutMs;
    return Math.min(Date.now(), idleDeadline);
  }, [idleTimeoutMs]);

  const getSegmentElapsedSeconds = useCallback(() => {
    if (!segmentStartRef.current || pausedRef.current) {
      return 0;
    }
    return Math.max(0, Math.floor((getEffectiveNow() - segmentStartRef.current) / 1000));
  }, [getEffectiveNow]);

  const getTotalTime = useCallback(() => {
    return lastSavedRef.current + getSegmentElapsedSeconds();
  }, [getSegmentElapsedSeconds]);

  const getCurrentTime = useCallback(() => getTotalTime(), [getTotalTime]);

  useEffect(() => {
    if (!enabled || !courseId || !chapterId || !moduleId) {
      return;
    }

    let cancelled = false;
    let saveIntervalId: ReturnType<typeof setInterval> | null = null;
    let idleCheckId: ReturnType<typeof setInterval> | null = null;

    const persistTime = async (timeToSave: number) => {
      if (!courseId || !chapterId || !moduleId || timeToSave <= 0) {
        return;
      }

      try {
        await moduleTimeTrackingAPI.updateTime(
          courseId,
          chapterId,
          moduleType,
          moduleId,
          timeToSave
        );
      } catch (error) {
        console.error('Error saving module time:', error);
      }
    };

    const flushSegment = () => {
      if (!segmentStartRef.current || pausedRef.current) {
        return;
      }

      const total = lastSavedRef.current + Math.max(
        0,
        Math.floor((getEffectiveNow() - segmentStartRef.current) / 1000)
      );

      if (total > lastSavedRef.current) {
        lastSavedRef.current = total;
        void persistTime(total);
      }

      segmentStartRef.current = getEffectiveNow();
    };

    const pauseTracking = () => {
      if (pausedRef.current) {
        return;
      }

      if (segmentStartRef.current) {
        const total = lastSavedRef.current + Math.max(
          0,
          Math.floor((getEffectiveNow() - segmentStartRef.current) / 1000)
        );
        if (total > lastSavedRef.current) {
          lastSavedRef.current = total;
          void persistTime(total);
        }
      }

      pausedRef.current = true;
      segmentStartRef.current = null;
    };

    const resumeTracking = () => {
      if (!pausedRef.current) {
        return;
      }
      pausedRef.current = false;
      segmentStartRef.current = Date.now();
    };

    const markActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;

      if (now - lastActivityHandledAtRef.current < ACTIVITY_THROTTLE_MS) {
        if (pausedRef.current) {
          resumeTracking();
        }
        return;
      }
      lastActivityHandledAtRef.current = now;

      if (pausedRef.current) {
        resumeTracking();
      }
    };

    const onActivity = () => markActivity();
    const activityEvents: Array<keyof DocumentEventMap> = [
      'pointerdown',
      'pointermove',
      'keydown',
      'scroll',
      'wheel',
      'touchstart',
      'click',
    ];

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        markActivity();
      }
    };

    const startTracking = () => {
      if (cancelled) return;

      pausedRef.current = false;
      lastActivityRef.current = Date.now();
      lastActivityHandledAtRef.current = 0;
      segmentStartRef.current = Date.now();

      activityEvents.forEach((eventName) => {
        document.addEventListener(eventName, onActivity, { passive: true, capture: true });
      });
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('focus', onActivity);

      saveIntervalId = setInterval(() => {
        if (pausedRef.current || !segmentStartRef.current) {
          return;
        }
        if (Date.now() >= lastActivityRef.current + idleTimeoutMs) {
          pauseTracking();
          return;
        }
        flushSegment();
      }, saveInterval * 1000);

      idleCheckId = setInterval(() => {
        if (pausedRef.current || !segmentStartRef.current) {
          return;
        }
        if (Date.now() >= lastActivityRef.current + idleTimeoutMs) {
          pauseTracking();
        }
      }, IDLE_CHECK_MS);
    };

    const loadAndStart = async () => {
      try {
        const response = await moduleTimeTrackingAPI.getTime(
          courseId,
          chapterId,
          moduleType,
          moduleId
        );
        if (cancelled) return;
        lastSavedRef.current = response?.timeSpent ?? 0;
      } catch (error: any) {
        if (cancelled) return;
        if (error?.response?.status !== 404) {
          console.error('Error loading existing time:', error);
        }
        lastSavedRef.current = 0;
      }

      startTracking();
    };

    void loadAndStart();

    return () => {
      cancelled = true;

      activityEvents.forEach((eventName) => {
        document.removeEventListener(eventName, onActivity, true);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onActivity);

      if (saveIntervalId) clearInterval(saveIntervalId);
      if (idleCheckId) clearInterval(idleCheckId);

      if (!pausedRef.current && segmentStartRef.current) {
        const total = lastSavedRef.current + Math.max(
          0,
          Math.floor((getEffectiveNow() - segmentStartRef.current) / 1000)
        );
        if (total > lastSavedRef.current) {
          lastSavedRef.current = total;
          void persistTime(total);
        }
      }

      segmentStartRef.current = null;
    };
  }, [
    enabled,
    courseId,
    chapterId,
    moduleType,
    moduleId,
    saveInterval,
    idleTimeoutMs,
    getEffectiveNow,
  ]);

  return {
    getCurrentTime,
  };
};
